import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';

// Trailing slashes are stripped. One in the deployed REACT_APP_API_URL is
// invisible in every normal request (axios collapses it against a path)
// but survives anywhere a URL is built by hand, and Express does not match
// a doubled slash — which is how opening a candidate's file came back as
// "Cannot GET //api/hiring/attachments/…/stream".
const apiClient = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || '').replace(/\/+$/, ''),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'x-client-type': 'web',
    'ngrok-skip-browser-warning': 'true',
  },
});

// Request interceptor — attach the bearer token.
//
// Read from the store FIRST. The token used to be read only from the bare
// `localStorage.token` key while `ProtectedRoute` and the login screen read
// the zustand-persisted copy in `auth-storage`, so the app could believe it
// was signed in while every request went out with no Authorization header
// at all. That divergence is what produced the sign-in loop below.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token || localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor — one place where an expired session ends.
//
// The bug this replaces: on a 401 it removed `localStorage.token` and did
// `window.location.href = '/login'`. That full reload rehydrated zustand
// from `auth-storage`, which still said `isAuthenticated: true` with the
// dead token, so the login screen's effect bounced straight to /dashboard,
// whose first request 401'd — with no token to send at all now — and round
// it went. A hard reload every lap, which is the flicker you saw.
//
// Two rules fix it:
//
//   1. Clear BOTH copies, via the store, so the reload comes back signed
//      out and the login screen stays put.
//   2. Only redirect if we thought we were signed in. A 401 from the login
//      endpoint itself means "wrong password" — the old code reloaded the
//      page and threw away the error message the user needed to read.
apiClient.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      const { isAuthenticated, clearAuth } = useAuthStore.getState();
      const onLoginScreen = window.location.pathname.startsWith('/login');

      if (isAuthenticated) {
        clearAuth();
        if (!onLoginScreen) {
          // replace, not href: the page that just failed should not be
          // sitting in history waiting for the back button.
          window.location.replace('/login?expired=1');
        }
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;
